// this file added by Bearer Web Developer (ReservedOrdersState)
import { flow, getEnv, Instance, types, getParentOfType, cast } from "mobx-state-tree";
import { AppState, EnvType } from "../app-state";
import { error, log } from "../../app/log";
import reservedOrdersList from "./reservedOrdersList";
import { TReservedOrdersListResponse } from "./models/reservedOrdersList";
import { TReservedOrderDetails } from "./models/reservedOrderDetails";
import { TSortBasis } from "./models/sortBasis";
import { TRemoveOrderApiIntegrationResponse } from "./models/removeOrderApiIntegration";
import removeOrderApiIntegration from "./removeOrderApiIntegration";
import { ApprovalMethodEnum, initialEditOrderApiIntegrationData, TEditOrderApiIntegrationData, TEditOrderApiIntegrationDataValue, TEditOrderApiIntegrationResponse } from "./models/editOrderApiIntegration";
import editOrderApiIntegration from "./editOrderApiIntegration";
import { fetchDownloadUrl } from "../../api/bearer-api";
import setReservedOrder from "./setReservedOrder";
import confirmReservedOrderApiIntegration from "./confirmReservedOrderApiIntegration";
import { TConfirmReservedOrderApiIntegrationResponse } from "./models/confirmReservedOrderApiIntegration";
import { SearchItems } from "./models/searchItems";
import timeSensitiveManagement from "../../containers/reservedOrdersPopup/earliestTime/timeSensitiveManagement";
import { TimeSensitiveEnum } from "../scheduleOrderstate";
export enum ShortcutReservedOrdersPlan {
    A = "A", B = "B", ShowAtFirstForPlanA = "Show At First For Plan A", ShowAtFirstForPlanB = "Show At First For Plan B",
    None = "None"
};
export const ReservedOrdersState = types
    .model("ReservedOrdersState", {
        reservedOrdersList: types.maybeNull(types.array(types.frozen<TReservedOrderDetails>())),
        activeManageOrder: types.maybeNull(types.frozen<TReservedOrderDetails>()),
        activeManageOrderFromShortcut: types.optional(types.boolean, false),
        sortBasis: types.maybeNull(types.frozen<TSortBasis>()),
        reservedOrdersOpen: types.optional(types.boolean, false),
        search: types.optional(types.string, ''),
        searchItems: types.optional(types.frozen<SearchItems>(), { destination_address: true, destination_details: true, client_order_number: true, recipient_name: true, recipient_phone_number: true }),
        editOrderApiIntegrationData: types.optional(types.frozen<TEditOrderApiIntegrationData>(), initialEditOrderApiIntegrationData),
        activeEditOrder: types.optional(types.boolean, false),
        shortcutReservedOrdersPlan: types.optional(types.frozen<ShortcutReservedOrdersPlan>(), ShortcutReservedOrdersPlan.None),
        todayReservedOrdersList: types.maybeNull(types.array(types.frozen<TReservedOrderDetails>())),
    }).actions(self => {
        return {
            setReservedOrdersOpen(value: boolean) {
                if (value) {
                    if (self.activeManageOrderFromShortcut) {
                        self.activeManageOrderFromShortcut = false;
                    }
                }
                self.reservedOrdersOpen = value;
            },
            setSearch(value: string) {
                self.search = value;
            },
            setSearchItems(value: SearchItems) {
                self.searchItems = value;
            },
            setShortcutReservedOrdersPlan(value: ShortcutReservedOrdersPlan) {
                self.shortcutReservedOrdersPlan = value;
            },
            setActiveManageOrder(value: TReservedOrderDetails | null, shortcut?: boolean) {
                if (!self?.reservedOrdersOpen) {
                    self.reservedOrdersOpen = true;
                }
                self.activeManageOrder = value;
                if (shortcut) {
                    self.activeManageOrderFromShortcut = true;
                } else if (self.activeManageOrderFromShortcut) {
                    self.activeManageOrderFromShortcut = false;
                }
            },
            setSortBasis(value: TSortBasis | null) {
                self.sortBasis = value;
            },
            setEditOrderApiIntegrationData(value: TEditOrderApiIntegrationDataValue) {
                self.editOrderApiIntegrationData = { ...initialEditOrderApiIntegrationData, ...self.editOrderApiIntegrationData, ...value };
            },
            sortReservedOrdersList(sortBasis?: TSortBasis) {
                const sortedData = self.reservedOrdersList?.map(r => r).sort(function compareNumbers(a, b) {
                    if (sortBasis === 'expiry_time') {
                        return (!a?.reservation_expiry_time ? 1 : !b?.reservation_expiry_time ? -1 : Number(a?.reservation_expiry_time) - Number(b?.reservation_expiry_time));
                    } else if (sortBasis === 'creation_time') {
                        return (!a?.transport_creation_time ? 1 : !b?.transport_creation_time ? -1 : Number(a?.transport_creation_time) - Number(b?.transport_creation_time));
                    } else {
                        // Sort By Order Due 
                        // Sort ascending by earliest_pickup_time, placing orders without this field at the end - items with null earliest_pickup_time values always come last
                        return (!a?.earliest_pickup_time ? 1 : !b?.earliest_pickup_time ? -1 : Number(a?.earliest_pickup_time) - Number(b?.earliest_pickup_time));
                    }
                })
                self.reservedOrdersList = cast(sortedData);
            },
            setActiveEditOrder(value: boolean) {
                self.activeEditOrder = value;
            },
            closeAll() {
                if (self.activeEditOrder) self.activeEditOrder = false;
                self.activeManageOrder = null;
                self.reservedOrdersOpen = false;
                self.sortBasis = null;
            }
        }
    }).actions(self => {
        return {
            fetchReservedOrdersList: flow(function* (fetchAtFirst?: boolean) {
                const api = getEnv<EnvType>(self).api;

                ///// To solve AppCheck - WebApp Issue on Loading App State and Tabs if any
                api.callApiWithAppCheck();

                try {
                    const result: TReservedOrdersListResponse = yield reservedOrdersList();
                    if (result.code === 200) {
                        const transports: TReservedOrderDetails[] = Object.keys(result?.transports).map(id => result?.transports[id]);
                        console.log({ reservedOrdersList: transports });
                        self.reservedOrdersList = cast(transports);
                        self.sortReservedOrdersList("Order Due");
                        // todayReservedOrders management start
                        const todayReservedOrders: TReservedOrderDetails[] = transports?.filter(order => {
                            return (
                                order?.is_time_sensitive_order && order?.earliest_pickup_time &&
                                // timeSensitiveManagement(order?.earliest_pickup_time) === TimeSensitiveEnum.PastDue // added for test
                                (timeSensitiveManagement(order?.earliest_pickup_time) === TimeSensitiveEnum.DueToday ||
                                timeSensitiveManagement(order?.earliest_pickup_time) === TimeSensitiveEnum.DueTodayPastTime)
                            )
                        })
                        if (Number(todayReservedOrders?.length) > 0) {
                            self.todayReservedOrdersList = cast(todayReservedOrders);
                            if (Number(todayReservedOrders?.length) <= 3) {
                                self.shortcutReservedOrdersPlan = fetchAtFirst ? ShortcutReservedOrdersPlan.ShowAtFirstForPlanA : ShortcutReservedOrdersPlan.A;
                            } else {
                                self.shortcutReservedOrdersPlan = fetchAtFirst ? ShortcutReservedOrdersPlan.ShowAtFirstForPlanB : ShortcutReservedOrdersPlan.B;
                            }
                        } else {
                            self.shortcutReservedOrdersPlan = ShortcutReservedOrdersPlan.None;
                            if (self.todayReservedOrdersList !== null) self.todayReservedOrdersList = null;
                        }
                        // todayReservedOrders management end
                    } else if (result.code === 499 || result.code === 401 || result.code === 406 || result.code === 408 || result.code === 400 || result.code === 500) {
                        log(result.message || 'unknown error!'); // modify by Bearer Web Developer
                    }
                } catch (err: any) {
                    error(err); // modify by Bearer Web Developer
                }
            }),
            removeOrderApiIntegration: flow(function* (transport_id: string) {
                const api = getEnv<EnvType>(self).api;

                ///// To solve AppCheck - WebApp Issue on Loading App State and Tabs if any
                api.callApiWithAppCheck();

                try {
                    const result: TRemoveOrderApiIntegrationResponse = yield removeOrderApiIntegration(transport_id);
                    if (result.code === 200) {
                        // Update the Reserved orders List Call reservedOrdersListApiIntegration
                        self.setActiveManageOrder(null);
                    } else if (result.code === 401 || result.code === 499) {
                        // Show First View (Map) in Guest Mode (logout and close all popups)
                        self.reservedOrdersOpen = false;
                        const appState = getParentOfType(self, AppState);
                        appState.login.logout();
                    } else if (result.code === 402 || result.code === 410 || result.code === 422) { // 421 result code removed and added implicitly in following lines by Bearer Web Developer
                        // (Show message first) Show Reserved Orders View after OK is clicked Call reservedOrdersListApiIntegration
                        const appState = getParentOfType(self, AppState);
                        appState.setSnackMessage({
                            message: result.message || 'unknown error!',
                            handleClick: () => {
                                // Call reservedOrdersListApiIntegration
                                self.setActiveManageOrder(null);
                            }
                        });
                    } else if (result.code === 500) {
                        // User Log out (First Action), Left Button (OK -> Reload App)
                        // Right Button (Support -> W.S.URL)
                        const appState = getParentOfType(self, AppState);
                        appState.login.logout().then(() => {
                            appState.setSnackMessage({
                                message: result?.message || 'unknown error!',
                                hasSopportLink: true,
                                handleClick: () => {
                                    window.location.reload();
                                }
                            })
                        }).catch(er => console.error(er));
                    } else if (result.code === 421) { // 421 result code added implicitly by Bearer Web Developer start
                        window.location.reload(); 
                    } // 421 result code added implicitly by Bearer Web Developer end
                } catch (err: any) {
                    error(err); // modify by Bearer Web Developer
                }
            }),
            editOrderApiIntegration: flow(function* (editOrderApiIntegrationData: TEditOrderApiIntegrationData) {
                const api = getEnv<EnvType>(self).api;

                ///// To solve AppCheck - WebApp Issue on Loading App State and Tabs if any
                api.callApiWithAppCheck();

                try {
                    const result: TEditOrderApiIntegrationResponse = yield editOrderApiIntegration(editOrderApiIntegrationData);

                    if (result.code === 200) {
                        // Update App view (Side Bar) with all details for this reserved order, Nothing
                        // in Side bar could not be Cleared / Edited / Removed / Replaced. The only
                        // Actions available and Allowed:
                        // 1. Select another Tab
                        // 2. Open a New Tab
                        // 3. View each section for the order (VIEW ONLY)
                        // 4. Apply Discount Code
                        // 5. Pay for the Order With/Without Discount Code

                        // Update activeManageOrder
                        // self.setActiveManageOrder(result?.new_transport); // It's not necessary
                        // remove this order from reservedOrdersList
                        const transport_id = editOrderApiIntegrationData?.transport_id;
                        const newReservedOrders = self.reservedOrdersList?.filter(order => order.transport_id !== transport_id);
                        self.reservedOrdersList = cast(newReservedOrders);
                        // remove this order from todayReservedOrders if it exists in todayReservedOrders list
                        // todayReservedOrders management start
                        const newtodayReservedOrders = self.todayReservedOrdersList?.filter(order => order.transport_id !== transport_id);
                        if (newtodayReservedOrders && Number(newtodayReservedOrders?.length > 0)) {
                            self.todayReservedOrdersList = cast(newtodayReservedOrders);
                            if (Number(newtodayReservedOrders?.length) <= 3) {
                                self.shortcutReservedOrdersPlan = ShortcutReservedOrdersPlan.A;
                            } else {
                                self.shortcutReservedOrdersPlan = ShortcutReservedOrdersPlan.B
                            }
                        } else {
                            self.shortcutReservedOrdersPlan = ShortcutReservedOrdersPlan.None;
                            if (self.todayReservedOrdersList !== null) self.todayReservedOrdersList = null;
                        }
                        // todayReservedOrders management end
                        const appState = getParentOfType(self, AppState) as any;
                        const login = appState.login.loggedin;
                        if ((login) || (!login && appState.orders.length <= 0)) {
                            yield (appState)?.addOrder();
                            appState.selectOrder(appState.orders.length - 1);
                            const new_transport = result?.new_transport;
                            const parcel_img_fetch_url = yield fetchDownloadUrl(new_transport?.parcel_img_url);
                            setReservedOrder(appState, { ...new_transport, parcel_img_fetch_url });
                        }
                        self.closeAll();
                        //const order = extractReservedOrder(reservedOrder,parceltypes);
                        return true;
                        // console.log({ editOrderApiIntegrationresult: result });
                    } else if (result.code === 401 || result.code === 499) {
                        // Show First View (Map) in Guest Mode (logout and close all popups)
                        const appState = getParentOfType(self, AppState);
                        appState.login.logout();
                    } else if (result.code === 402 || result.code === 410 || result.code === 422) { // 421 result code removed and added implicitly in following lines by Bearer Web Developer
                        // (Show message first) Show  Orders View after OK is clicked Call reservedOrdersListApiIntegration
                        const appState = getParentOfType(self, AppState);
                        appState.setSnackMessage({
                            message: result.message || 'unknown error!',
                            handleClick: () => {
                                // Call reservedOrdersListApiIntegration
                                self.setActiveManageOrder(null);
                            }
                        });
                    } else if (result.code === 400) {
                        // show message, 400 added (it is not in the logic that has been sent)
                        const appState = getParentOfType(self, AppState);
                        appState.setSnackMessage({
                            message: result?.message || 'unknown error!',
                        });
                    } else if (result.code === 500) {
                        // User Log out (First Action), Left Button (OK -> Reload App)
                        // Right Button (Support -> W.S.URL)
                        const appState = getParentOfType(self, AppState);
                        appState.login.logout().then(() => {
                            appState.setSnackMessage({
                                message: result?.message || 'unknown error!',
                                hasSopportLink: true,
                                handleClick: () => {
                                    window.location.reload();
                                }
                            })
                        }).catch(er => console.error(er));
                    } else if (result.code === 421) { // 421 result code added implicitly by Bearer Web Developer start
                        window.location.reload(); 
                    } // 421 result code added implicitly by Bearer Web Developer end
                } catch (err: any) {
                    error(err); // modify by Bearer Web Developer
                }
            }),
            confirmReservedOrderApiIntegration: flow(function* (transport_id: string) {
                const api = getEnv<EnvType>(self).api;

                ///// To solve AppCheck - WebApp Issue on Loading App State and Tabs if any
                api.callApiWithAppCheck();

                try {
                    const result: TConfirmReservedOrderApiIntegrationResponse = yield confirmReservedOrderApiIntegration(transport_id);
                    if (result?.code === 200) {
                        // Update activeManageOrder
                        // if (self.activeManageOrder) self.setActiveManageOrder(result.new_transport); // It's not necessary
                        // remove this order from reservedOrdersList
                        const newReservedOrders = self.reservedOrdersList?.filter(order => order.transport_id !== transport_id)
                        self.reservedOrdersList = cast(newReservedOrders);
                        // remove this order from todayReservedOrders if it exists in todayReservedOrders list
                        // todayReservedOrders management start
                        const newtodayReservedOrders = self.todayReservedOrdersList?.filter(order => order.transport_id !== transport_id);
                        if (newtodayReservedOrders && Number(newtodayReservedOrders?.length > 0)) {
                            self.todayReservedOrdersList = cast(newtodayReservedOrders);
                            if (Number(newtodayReservedOrders?.length) <= 3) {
                                self.shortcutReservedOrdersPlan = ShortcutReservedOrdersPlan.A;
                            } else {
                                self.shortcutReservedOrdersPlan = ShortcutReservedOrdersPlan.B
                            }
                        } else {
                            self.shortcutReservedOrdersPlan = ShortcutReservedOrdersPlan.None;
                            if (self.todayReservedOrdersList !== null) self.todayReservedOrdersList = null;
                        }
                        // todayReservedOrders management end
                        return result;
                    } else {
                        log(result);
                        // show message
                        const appState = getParentOfType(self, AppState);
                        appState.setSnackMessage({
                            message: result?.message || 'unknown error!',
                        });
                    }
                } catch (err: any) {
                    error(err); // modify by Bearer Web Developer
                }
            }),
            handleEdit(bool: boolean) {
                if (bool) {
                    // set initial edit data values
                    self?.setEditOrderApiIntegrationData({
                        transport_id: self?.activeManageOrder?.transport_id,
                        destination_details: self?.activeManageOrder?.destination_details,
                        recipient_name: self?.activeManageOrder?.recipient_name,
                        recipient_phone_number: self?.activeManageOrder?.recipient_phone_number,
                        dropoff_verification_method: self?.activeManageOrder?.dropoff_verification_method as ApprovalMethodEnum,
                    });
                } else {
                    self?.setEditOrderApiIntegrationData(initialEditOrderApiIntegrationData)
                }
                self?.setActiveEditOrder(bool);
            }
        }
    })
export interface IReservedOrdersState extends Instance<typeof ReservedOrdersState> { };

